import { BelongingPhotoField } from '@/components/BelongingPhotoField';
/**
 * Belongings section embedded on the booking detail page (sub-spec 06 Plan 5).
 *
 * Staff side: full CRUD (add, edit, mark returned, delete).
 * Customer side (readonly): just the list + returned indicator.
 *
 * The photoR2Key upload affordance is omitted until sub-spec 07 (files-and-
 * docs) ships its R2 upload endpoint. The list still renders any stored
 * photoR2Key as a placeholder badge.
 */
import {
  type BookingBelonging,
  createBookingBelonging,
  deleteBookingBelonging,
  listBookingBelongings,
  markBookingBelongingReturned,
} from '@/lib/api';
import { Check, ImageIcon, Plus, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

interface BelongingsSectionProps {
  bookingId: string;
  readonly?: boolean;
}

export function BelongingsSection({ bookingId, readonly = false }: BelongingsSectionProps) {
  const [items, setItems] = useState<BookingBelonging[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [draftPhotoFileId, setDraftPhotoFileId] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listBookingBelongings(bookingId);
      setItems(res.items);
    } catch (e) {
      setError((e as Error).message ?? 'Failed to load belongings');
    } finally {
      setLoading(false);
    }
  }, [bookingId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleAdd(e: React.FormEvent) {
    e.preventDefault();
    if (!draft.trim()) return;
    setBusy('add');
    setError(null);
    try {
      await createBookingBelonging(bookingId, {
        description: draft.trim(),
        ...(draftPhotoFileId ? { photoFileId: draftPhotoFileId } : {}),
      });
      setDraft('');
      setDraftPhotoFileId(null);
      await load();
    } catch (e) {
      setError((e as Error).message ?? 'Failed to add belonging');
    } finally {
      setBusy(null);
    }
  }

  async function handleReturn(id: string) {
    setBusy(`return-${id}`);
    setError(null);
    try {
      await markBookingBelongingReturned(bookingId, id);
      await load();
    } catch (e) {
      setError((e as Error).message ?? 'Failed to mark returned');
    } finally {
      setBusy(null);
    }
  }

  async function handleDelete(id: string) {
    if (!confirm('Remove this belonging entry?')) return;
    setBusy(`delete-${id}`);
    setError(null);
    try {
      await deleteBookingBelonging(bookingId, id);
      await load();
    } catch (e) {
      setError((e as Error).message ?? 'Failed to delete');
    } finally {
      setBusy(null);
    }
  }

  return (
    <section aria-labelledby="belongings-heading">
      <h3 id="belongings-heading" className="text-sm font-semibold mb-3">
        Belongings
      </h3>
      {error && (
        <p
          role="alert"
          className="mb-3 rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive"
        >
          {error}
        </p>
      )}
      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-muted-foreground">No belongings recorded.</p>
      ) : (
        <ul className="space-y-2 mb-3">
          {items.map((item) => (
            <li
              key={item.id}
              className="flex items-start justify-between gap-2 rounded-md border border-border px-3 py-2 text-sm"
            >
              <div className="min-w-0 flex-1">
                <p
                  className={
                    item.returnedAt
                      ? 'text-muted-foreground line-through break-words'
                      : 'break-words'
                  }
                >
                  {item.description}
                </p>
                {(item.photoFileId || item.photoR2Key) && (
                  <a
                    href={`/api/bookings/${bookingId}/belongings/${item.id}/photo`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 mt-1 text-xs text-muted-foreground hover:text-foreground underline"
                  >
                    <ImageIcon className="size-3" strokeWidth={1.75} />
                    View photo
                  </a>
                )}
                {item.returnedAt && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Returned {new Date(item.returnedAt).toLocaleString('en-GB')}
                  </p>
                )}
              </div>
              {!readonly && (
                <div className="flex items-center gap-1 shrink-0">
                  {!item.returnedAt && (
                    <button
                      type="button"
                      aria-label="Mark returned"
                      disabled={busy === `return-${item.id}`}
                      onClick={() => handleReturn(item.id)}
                      className="rounded-md p-1 text-muted-foreground hover:bg-secondary hover:text-foreground disabled:opacity-50"
                    >
                      <Check className="size-4" strokeWidth={1.75} />
                    </button>
                  )}
                  <button
                    type="button"
                    aria-label="Delete"
                    disabled={busy === `delete-${item.id}`}
                    onClick={() => handleDelete(item.id)}
                    className="rounded-md p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive disabled:opacity-50"
                  >
                    <Trash2 className="size-4" strokeWidth={1.75} />
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      {!readonly && (
        <form onSubmit={handleAdd} className="space-y-2">
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="e.g. Red leash + collar"
              aria-label="New belonging description"
              maxLength={500}
              className="flex-1 rounded-md border border-border bg-surface px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
            />
            <button
              type="submit"
              disabled={busy === 'add' || !draft.trim()}
              className="inline-flex items-center gap-1 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground disabled:opacity-50"
            >
              <Plus className="size-4" strokeWidth={1.75} />
              Add
            </button>
          </div>
          {/* Sub-spec 07 Plan 3 — inline photo attachment */}
          <BelongingPhotoField bookingId={bookingId} onPhotoChange={setDraftPhotoFileId} />
        </form>
      )}
    </section>
  );
}
